import { useTranslation } from 'react-i18next';

import { Badge } from '@codes/ui-kit/components/ui/badge';
import { Button } from '@codes/ui-kit/components/ui/button';
import { PageContainer } from '@web/app/shared/components/PageContainer';
import { PlusIcon } from 'lucide-react';

import { SharedSpinner } from '@codes/ui-kit';

import {
    CodesKeyCard,
    CodesKeyDeleteDialog,
    CodesKeyEmptyState,
    CodesKeyFormDialog,
    CodesKeyRegenerateDialog,
    CodesKeySuccessDialog,
    CodesKeyValidationDialog,
} from '../components';
import { useCodesKeys } from '../hooks';
import { maskApiKey } from '../utils';

export const CodesKeysPage = () => {
    const { t } = useTranslation();

    const {
        keysData,
        isKeysLoading,
        isKeysError,
        keysError,
        callbackInfo,
        formDialog,
        successDialog,
        deleteDialog,
        regenerateDialog,
        validationDialog,
        handleCreate,
        handleEdit,
        handleDelete,
        handleRegenerate,
        handleValidate,
    } = useCodesKeys();

    if (isKeysLoading) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                <SharedSpinner />
            </div>
        );
    }

    if (isKeysError) {
        return (
            <div className="flex items-center justify-center h-[calc(100vh-200px)]">
                <div className="text-center">
                    <p className="text-destructive text-lg font-semibold mb-2">{t('codesKeys.errorLoadingKeys')}</p>
                    <p className="text-muted-foreground text-sm">{keysError?.message || t('common.unexpectedError')}</p>
                </div>
            </div>
        );
    }

    const keys = keysData?.list || [];

    return (
        <div className="flex-1 bg-background min-h-0 w-full flex flex-col animate-enter">
            <PageContainer noPadding className="flex-1 py-8 px-8 max-md:px-4 max-md:pt-6">
                <div className="mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-2">
                            <h1 className="text-lg font-display font-bold">{t('codesKeys.title')}</h1>
                            <Badge variant="success">Beta</Badge>
                        </div>
                        <Button onClick={handleCreate}>
                            <PlusIcon className="mr-2 h-4 w-4" />
                            {t('codesKeys.createNew')}
                        </Button>
                    </div>

                    {keys.length === 0 ? (
                        <CodesKeyEmptyState onCreateClick={handleCreate} />
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                            {keys.map(key => (
                                <CodesKeyCard
                                    key={key.id || key.apiKey}
                                    data={key}
                                    onEdit={handleEdit}
                                    onDelete={handleDelete}
                                    onRegenerate={handleRegenerate}
                                    onValidate={handleValidate}
                                    callbackInfo={callbackInfo}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </PageContainer>

            <CodesKeyFormDialog
                open={formDialog.isOpen}
                onOpenChange={formDialog.setIsOpen}
                mode={formDialog.mode}
                keyData={formDialog.selectedKey}
                onSuccess={formDialog.onSuccess}
            />

            {successDialog.newlyCreatedKey && (
                <CodesKeySuccessDialog
                    open={successDialog.isOpen}
                    onOpenChange={successDialog.setIsOpen}
                    apiKey={successDialog.newlyCreatedKey.apiKey}
                    keyName={successDialog.newlyCreatedKey.name}
                    callbackInfo={callbackInfo}
                />
            )}

            {deleteDialog.keyToDelete && (
                <CodesKeyDeleteDialog
                    open={deleteDialog.isOpen}
                    onOpenChange={deleteDialog.setIsOpen}
                    keyName={deleteDialog.keyToDelete.name || deleteDialog.keyToDelete.id || 'key'}
                    isDeleting={deleteDialog.isDeleting}
                    onConfirm={deleteDialog.onConfirm}
                />
            )}

            {regenerateDialog.keyToRegenerate && (
                <CodesKeyRegenerateDialog
                    open={regenerateDialog.isOpen}
                    onOpenChange={regenerateDialog.setIsOpen}
                    keyName={regenerateDialog.keyToRegenerate.name || regenerateDialog.keyToRegenerate.id || 'key'}
                    maskedKey={maskApiKey(regenerateDialog.keyToRegenerate.apiKey || '')}
                    isRegenerating={regenerateDialog.isRegenerating}
                    onConfirm={regenerateDialog.onConfirm}
                />
            )}

            <CodesKeyValidationDialog
                open={validationDialog.isOpen}
                onOpenChange={validationDialog.setIsOpen}
                keyToValidate={validationDialog.keyToValidate}
            />
        </div>
    );
};
